// src/native/compliance/useAiConsent.js
//
// WS-7 COMPLIANCE. React binding over aiConsentStore.
//
// The state is SEEDED SYNCHRONOUSLY from localStorage, so the first render
// already knows the answer and the gate never flashes over an app the user has
// already agreed in. The async pass runs afterwards and only corrects the rare
// cases the synchronous read cannot see: a reinstall, or an account that
// accepted on another device. That ordering matters because this now runs on
// the web, in a funnel where a frame of the wrong UI is a real cost.

import { useCallback, useEffect, useState } from 'react';
import { isAiConsentRequiredHere } from './consentConfig';
import {
  AI_CONSENT_CHANGED_EVENT,
  acceptAiConsentWithAffirmation,
  getAiConsent,
  getMinorsAck,
  peekConsentSync,
  setAiConsent,
  setMinorsAck,
} from './aiConsentStore';

export function useAiConsent() {
  const required = isAiConsentRequiredHere();

  const [state, setState] = useState(() => {
    const seed = peekConsentSync();
    return {
      // Never true. The synchronous seed is already an answer, and a loading
      // state here would mean one frame of nothing on every single page load.
      loading: false,
      required: seed.required,
      granted: seed.granted,
      decided: seed.decided,
      minorsDone: seed.minorsDone,
      record: null,
      minorsRecord: null,
    };
  });

  const refresh = useCallback(async () => {
    if (!required) {
      setState({
        loading: false,
        required: false,
        granted: true,
        decided: true,
        minorsDone: true,
        record: null,
        minorsRecord: null,
      });
      return;
    }
    const [consent, minors] = await Promise.all([getAiConsent(), getMinorsAck()]);
    setState({
      loading: false,
      required: true,
      granted: consent.granted,
      decided: consent.decided,
      minorsDone: minors.acknowledged,
      record: consent.record,
      minorsRecord: minors.record,
    });
  }, [required]);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) void refresh();
    };
    run();
    window.addEventListener(AI_CONSENT_CHANGED_EVENT, run);
    return () => {
      alive = false;
      window.removeEventListener(AI_CONSENT_CHANGED_EVENT, run);
    };
  }, [refresh]);

  /** The one tap accept: minors affirmation and AI consent together. */
  const accept = useCallback(async (meta) => {
    await acceptAiConsentWithAffirmation(meta);
  }, []);

  /** Consent only, used where the affirmation is already on record. */
  const grant = useCallback(async (meta) => {
    await setAiConsent(true, meta);
  }, []);

  const revoke = useCallback(async (meta) => {
    await setAiConsent(false, meta);
  }, []);

  const acknowledgeMinors = useCallback(async (answer) => {
    await setMinorsAck(answer);
  }, []);

  return { ...state, refresh, accept, grant, revoke, acknowledgeMinors };
}

export default useAiConsent;
